export const WORD_ART_REF_WIDTH = 320;
export const WORD_ART_REF_HEIGHT = 100;

export const getWordArtScale = (containerWidth, containerHeight) => {
  const safeWidth = Math.max(containerWidth, 1);
  const safeHeight = Math.max(containerHeight, 1);
  return {
    scaleX: safeWidth / WORD_ART_REF_WIDTH,
    scaleY: safeHeight / WORD_ART_REF_HEIGHT,
  };
};

export const deltaToPlainText = (delta) => {
  if (!delta?.ops) return '';
  return delta.ops
    .map((op) => (typeof op.insert === 'string' ? op.insert : ''))
    .join('')
    .replace(/\n$/, '');
};

export const plainTextToDelta = (text) => ({
  ops: [{ insert: text || '' }, { insert: '\n' }],
});

export const buildWordArtStyles = (effects = {}) => {
  const isGradientFill = effects.fillType === 'gradient' && effects.gradient;
  const styles = {
    fontFamily: effects.fontFamily || 'Roboto, sans-serif',
    fontSize: effects.fontSize || '48px',
    fontWeight: effects.fontWeight || '700',
    letterSpacing: effects.letterSpacing || 'normal',
    textTransform: effects.textTransform || 'none',
    lineHeight: 1.1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    textAlign: 'center',
    display: isGradientFill ? 'inline-block' : 'block',
    width: isGradientFill ? 'auto' : '100%',
    maxWidth: '100%',
  };

  if (effects.transform3d) {
    styles.transform = effects.transform3d;
    styles.transformOrigin = 'center center';
  }

  if (isGradientFill) {
    styles.background = effects.gradient;
    styles.WebkitBackgroundClip = 'text';
    styles.backgroundClip = 'text';
    styles.WebkitTextFillColor = 'transparent';
    styles.color = 'transparent';
    // text-shadow on gradient-clipped text renders as black glyphs in Chrome/Edge
  } else {
    styles.color = effects.fillColor || '#202124';
  }

  if (effects.stroke?.width) {
    styles.WebkitTextStroke = `${effects.stroke.width} ${effects.stroke.color || '#000'}`;
  }

  const shadows = [];
  if (!isGradientFill && effects.shadow) shadows.push(effects.shadow);
  if (!isGradientFill && effects.glow?.size) {
    const c = effects.glow.color || '#4285f4';
    const s = effects.glow.size;
    shadows.push(`0 0 ${s} ${c}, 0 0 ${parseInt(s, 10) * 2}px ${c}`);
  }
  if (shadows.length) {
    styles.textShadow = shadows.join(', ');
  }

  if (effects.opacity != null) {
    styles.opacity = effects.opacity;
  }

  return styles;
};

/** Shadow/glow as filter on a wrapper — safe to combine with gradient text fill */
export const buildWordArtShadowFilter = (effects = {}) => {
  const filters = [];
  if (effects.shadow) {
    filters.push(`drop-shadow(${effects.shadow})`);
  }
  if (effects.glow?.size) {
    const c = effects.glow.color || '#4285f4';
    const s = effects.glow.size;
    filters.push(`drop-shadow(0 0 ${s} ${c})`);
    filters.push(`drop-shadow(0 0 ${parseInt(s, 10) * 2}px ${c})`);
  }
  return filters.length ? filters.join(' ') : undefined;
};

/** Scale preset font size to fit the WordArt bounding box. */
export const scaleWordArtStyles = (effects = {}, containerWidth = 320, containerHeight = 100, text = '') => {
  const styles = buildWordArtStyles(effects);
  const baseFontSize =
    parseInt(String(effects.fontSize || '48').replace('px', ''), 10) || 48;
  const charCount = Math.max(String(text || 'A').replace(/\s/g, '').length, 1);
  const safeWidth = Math.max(containerWidth, 40);
  const safeHeight = Math.max(containerHeight, 24);
  const maxByWidth = (safeWidth * 0.9) / (charCount * 0.55);
  const maxByHeight = safeHeight * 0.72;
  const scaledSize = Math.max(12, Math.min(baseFontSize, maxByWidth, maxByHeight));

  styles.fontSize = `${Math.round(scaledSize)}px`;
  return styles;
};

export const buildWordArtDisplayStyles = (
  effects = {},
  containerWidth = 320,
  containerHeight = 100,
  text = ''
) => {
  const textStyles = scaleWordArtStyles(effects, containerWidth, containerHeight, text);
  const wrapperStyle = {};
  const shadowFilter = buildWordArtShadowFilter(effects);

  if (effects.fillType === 'gradient' && effects.gradient) {
    delete textStyles.textShadow;
    if (shadowFilter) {
      wrapperStyle.filter = shadowFilter;
    }
  }

  return { textStyles, wrapperStyle };
};

const SCALED_STYLE_KEYS = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'letterSpacing',
  'textTransform',
  'lineHeight',
  'whiteSpace',
  'wordBreak',
  'textAlign',
  'display',
  'width',
  'transform',
  'transformOrigin',
  'background',
  'WebkitBackgroundClip',
  'backgroundClip',
  'WebkitTextFillColor',
  'color',
  'WebkitTextStroke',
  'textShadow',
  'opacity',
];

export const applyWordArtStyles = (element, styles = {}) => {
  if (!element) return;
  SCALED_STYLE_KEYS.forEach((key) => {
    const value = styles[key];
    element.style[key] = value == null ? '' : value;
  });
};

export const isSameWordArtFontSize = (prevStyles = {}, nextStyles = {}) =>
  prevStyles.fontSize === nextStyles.fontSize &&
  prevStyles.background === nextStyles.background &&
  prevStyles.color === nextStyles.color;

export const getWordArtEditStyles = (displayStyles = {}) => ({
  ...displayStyles,
  caretColor: '#202124',
  cursor: 'text',
});

export const hasReflection = (effects) => Boolean(effects?.reflection);
